import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { FaDownload, FaTrash } from "react-icons/fa";
import api from "../../../api";
import { Pill } from "../performance/MyKraKpiPerformancePage";
import { formatMonthKey } from "../performance/performanceFormat";
import { applyFieldRules, formatFieldValue, isFieldLocked, recordEvidenceMeta, recordValue, statusLabel } from "./kraWorkFormat";

// Add / edit / view one "My KRA Work" record. The form is rendered from the
// record type's server-side field schema (GET /analyst-work/meta), so the
// fields, required flags and options always match what the API validates.
// Schools come from the existing schools master (GET /schools).
// Saving a record never submits anything for scoring -- that is "Prepare
// evidence" on the KRA work page, which goes to the reviewer.

async function downloadFile(url, name) {
  const res = await api.get(url, { responseType: "blob" });
  const href = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = name || "attachment";
  a.click();
  window.URL.revokeObjectURL(href);
}

function initialValues(typeMeta, record, month) {
  const values = {};
  for (const field of typeMeta.fields) {
    values[field.key] = recordValue(record, field);
    if (!record && field.type === "MONTH" && month) values[field.key] = month;
  }
  values.status = record?.status || typeMeta.statuses[0]?.value || "";
  return applyFieldRules(typeMeta.fields, values);
}

function FieldInput({ field, value, onChange, schools, disabled, max, hint }) {
  const id = `wr-${field.key}`;
  const label = <Form.Label>{field.label}{field.required && <span className="text-danger"> *</span>}</Form.Label>;
  if (field.type === "BOOLEAN") {
    return <Form.Check className="mb-3" id={id} type="checkbox" label={field.label} checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
  }
  let control;
  if (field.type === "SELECT" || field.type === "SCHOOL") {
    const options = field.type === "SCHOOL" ? schools.map((s) => ({ value: String(s.id), label: s.name })) : field.options;
    control = (
      <Form.Select value={value === null || value === undefined ? "" : String(value)} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{field.type === "SCHOOL" ? "Select school…" : "Select…"}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Form.Select>
    );
  } else if (field.type === "TEXTAREA") {
    control = <Form.Control as="textarea" rows={2} value={value} maxLength={field.max_length || undefined} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  } else {
    const inputType = { DATE: "date", DATETIME: "datetime-local", MONTH: "month", INTEGER: "number", NUMBER: "number", URL: "url" }[field.type] || "text";
    // `suggestions` = pick-list that still accepts free text (native datalist).
    const listId = field.suggestions?.length ? `${id}-list` : undefined;
    control = (
      <>
        <Form.Control
          type={inputType} value={value} disabled={disabled}
          min={field.type === "INTEGER" || field.type === "NUMBER" ? 0 : undefined}
          max={max}
          step={field.type === "INTEGER" ? 1 : field.type === "NUMBER" ? "any" : undefined}
          maxLength={field.max_length || undefined}
          placeholder={field.type === "URL" ? "https://…" : listId ? "Select or type…" : undefined}
          list={listId}
          onChange={(e) => onChange(e.target.value)}
        />
        {listId && (
          <datalist id={listId}>
            {field.suggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
        )}
      </>
    );
  }
  return (
    <Form.Group className="mb-3" controlId={id}>
      {label}{control}
      {hint && <Form.Text muted>{hint}</Form.Text>}
    </Form.Group>
  );
}

const WorkRecordModal = ({ typeMeta, record, month, schools = [], readOnly = false, onClose, onSaved }) => {
  const [values, setValues] = useState(() => initialValues(typeMeta, record, month));
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(!readOnly);
  const isNew = !record;
  const locked = !!record?.is_locked;
  const disabled = !editing || locked || saving;

  useEffect(() => {
    if (!record?.id) return undefined;
    let cancelled = false;
    api.get(`/analyst-work/records/${record.id}`)
      .then((res) => { if (!cancelled) setAttachments(res.data?.data?.attachments || []); })
      .catch(() => { if (!cancelled) setAttachments([]); });
    return () => { cancelled = true; };
  }, [record?.id]);

  const missing = useMemo(
    () => typeMeta.fields.filter((f) => f.required && (values[f.key] === "" || values[f.key] === null || values[f.key] === undefined)),
    [typeMeta, values]
  );

  const save = async () => {
    if (missing.length) { setError(`Enter "${missing[0].label}".`); return; }
    setSaving(true);
    setError("");
    try {
      const body = { record_type: typeMeta.record_type, status: values.status };
      for (const field of typeMeta.fields) body[field.key] = values[field.key] === "" ? null : values[field.key];
      const res = isNew
        ? await api.post("/analyst-work/records", body)
        : await api.put(`/analyst-work/records/${record.id}`, body);
      const saved = res.data?.data;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/analyst-work/records/${saved.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSaved?.(saved);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save this record.");
    } finally {
      setSaving(false);
    }
  };

  const removeAttachment = async (id) => {
    try {
      await api.delete(`/analyst-work/attachments/${id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the attachment.");
    }
  };

  const evidence = record ? recordEvidenceMeta(record) : null;

  return (
    <Modal show onHide={saving ? undefined : onClose} centered size="lg" className="kp-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title as="div">
          <div className="tlp-eyebrow">{typeMeta.label}{record ? ` · ${formatMonthKey(record.period_month)}` : ""}</div>
          <div className="kp-modal-title">{isNew ? "Add record" : record.title}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {record && (
          <div className="kw-modal-badges">
            <Pill meta={evidence} />
            {locked && <span className="kp-sub">Locked: this record is part of KPI evidence that is awaiting review or approved.</span>}
          </div>
        )}
        <p className="kp-sub mb-3">Counts toward: <strong>{typeMeta.kpi_hint}</strong>. {typeMeta.qualifying_note}</p>

        {!editing ? (
          <dl className="kw-details">
            {typeMeta.fields.map((field) => (
              <React.Fragment key={field.key}>
                <dt>{field.label}</dt>
                <dd>{field.type === "URL" && recordValue(record, field)
                  ? <a href={recordValue(record, field)} target="_blank" rel="noopener noreferrer">{recordValue(record, field)}</a>
                  : formatFieldValue(field, recordValue(record, field), { schools, record })}</dd>
              </React.Fragment>
            ))}
            <dt>Status</dt>
            <dd>{statusLabel(typeMeta, record?.status)}</dd>
          </dl>
        ) : (
          <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
            <div className="kw-form-grid">
              {typeMeta.fields.map((field) => {
                const fieldLocked = isFieldLocked(field, values);
                const capLabel = field.max_of && typeMeta.fields.find((f) => f.key === field.max_of)?.label;
                return (
                  <div key={field.key} className={field.type === "TEXTAREA" ? "kw-span-2" : ""}>
                    <FieldInput field={field} value={values[field.key]} schools={schools} disabled={disabled || fieldLocked}
                      max={field.max_of && values[field.max_of] !== "" ? values[field.max_of] : undefined}
                      hint={fieldLocked ? `0 — no ${String(capLabel || "incorrect records").toLowerCase()}.`
                        : field.computed_from ? "Filled automatically (total − accurate)." : undefined}
                      onChange={(v) => setValues((prev) => applyFieldRules(typeMeta.fields, { ...prev, [field.key]: v }, field.key))} />
                  </div>
                );
              })}
              {typeMeta.statuses.length > 1 && (
                <Form.Group className="mb-3" controlId="wr-status">
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={values.status} disabled={disabled} onChange={(e) => setValues((prev) => ({ ...prev, status: e.target.value }))}>
                    {typeMeta.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                </Form.Group>
              )}
            </div>
            {!locked && (
              <Form.Group className="mb-2" controlId="wr-files">
                <Form.Label>Attach evidence</Form.Label>
                <Form.Control type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))} disabled={saving} />
                <Form.Text muted>PDF, image, Word, Excel or text — up to 10 MB each.</Form.Text>
              </Form.Group>
            )}
          </Form>
        )}

        {attachments.length > 0 && (
          <div className="kw-attachments">
            <strong className="kp-sub">Attachments</strong>
            <ul className="mb-0">
              {attachments.map((a) => (
                <li key={a.id}>
                  <button type="button" className="kp-link" onClick={() => downloadFile(`/analyst-work/attachments/${a.id}/download`, a.original_name)}>
                    <FaDownload /> {a.original_name}
                  </button>
                  {editing && !locked && (
                    <button type="button" className="kp-link kw-remove" onClick={() => removeAttachment(a.id)} aria-label={`Remove ${a.original_name}`}>
                      <FaTrash />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="tlp-btn tlp-btn-ghost" onClick={onClose} disabled={saving}>Close</button>
        {!editing && record?.can_edit && (
          <button type="button" className="tlp-btn tlp-btn-primary" onClick={() => setEditing(true)}>Edit</button>
        )}
        {editing && !locked && (
          <button type="button" className="tlp-btn tlp-btn-primary" onClick={save} disabled={saving}>
            {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : isNew ? "Save record" : "Save changes"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WorkRecordModal;
